import React from 'react';
import { MainLayout } from '../../../core/layouts/MainLayout';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useProjects } from '../../../core/api';
import { LoadingSpinner } from '../../../core/components/LoadingSpinner';
import { MapPin, ArrowRight, Building2 } from 'lucide-react';

/** Strip HTML tags from rich-text editor output for plain-text previews */
const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const PublicProjectsPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const { data: projects, isLoading } = useProjects({});

    if (isLoading) return (
        <MainLayout>
            <div className="flex items-center justify-center min-h-[50vh]">
                <LoadingSpinner size="lg" />
            </div>
        </MainLayout>
    );

    return (
        <MainLayout>
            <div className="min-h-screen bg-slate-50 pt-24 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Practical Header */}
                    <div className="mb-8 border-b border-slate-200 pb-6">
                        <h1 className="text-3xl font-bold text-slate-900">
                            {t('projects.pageTitle', 'All Projects')}
                        </h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            {t('projects.pageSubtitle', 'Browse our complete list of development projects. Select a project to view available units, floor plans, and location details.')}
                        </p>
                    </div>

                    {/* Practical Grid Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Ensure projects is an array before mapping */}
                        {Array.isArray(projects) && projects.map((project: any) => (
                            <Link
                                key={project.id}
                                to={`/projects/${project.slug || project.id}`}
                                className="group bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden hover:shadow-md hover:border-primary/50 transition-all duration-200 flex flex-col h-full"
                            >
                                {/* Image Container - uniform aspect ratio */}
                                <div className="aspect-[16/9] w-full relative bg-slate-100 overflow-hidden">
                                    {project.image ? (
                                        <img
                                            src={project.image}
                                            alt={project.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                                            <Building2 className="w-12 h-12 opacity-20" />
                                        </div>
                                    )}

                                    {/* Availability Badge */}
                                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-0.5 text-xs font-semibold text-slate-700 rounded-md shadow-sm border border-slate-100">
                                        {project.status || 'Available'}
                                    </div>
                                </div>

                                {/* Content Body */}
                                <div className="p-5 flex-1 flex flex-col">
                                    <div className="mb-3">
                                        <h3 className="text-xl font-bold text-slate-900 group-hover:text-primary transition-colors line-clamp-1 mb-1">
                                            {project.title}
                                        </h3>
                                        <div className="flex items-center text-sm text-slate-500">
                                            <MapPin className="w-4 h-4 mr-1.5 flex-shrink-0 text-slate-400" />
                                            <span className="line-clamp-1">{project.location?.name || project.address || t('common.location', 'Location')}</span>
                                        </div>
                                    </div>

                                    <p className="text-sm text-slate-600 line-clamp-3 mb-4 flex-1 leading-relaxed">
                                        {stripHtml(project.description || '')}
                                    </p>

                                    {/* Card Footer */}
                                    <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
                                        <div className="text-slate-500 font-medium bg-slate-50 px-2 py-1 rounded">
                                            <span className="text-slate-900 font-bold mr-1">{project.units_count || 0}</span>
                                            {t('common.units', 'Units')}
                                        </div>

                                        <span className="text-primary font-semibold flex items-center group-hover:translate-x-1 transition-transform rtl:group-hover:-translate-x-1">
                                            {isRTL ? 'عرض التفاصيل' : 'View Details'}
                                            <ArrowRight className={clsx("w-4 h-4 ml-1.5", isRTL && "rotate-180")} />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Empty State */}
                    {(!isLoading && (!projects || !Array.isArray(projects) || projects.length === 0)) && (
                        <div className="text-center py-20 bg-white rounded-lg border border-dashed border-slate-300">
                            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                            <h3 className="text-lg font-medium text-slate-900">{t('projects.empty', 'No projects found')}</h3>
                            <p className="text-slate-500 mt-1">{t('projects.emptySubtitle', 'Check back later for new developments.')}</p>
                        </div>
                    )}
                </div>
            </div>
        </MainLayout>
    );
};
export default PublicProjectsPage;
